import React, { useState } from 'react';
import ConfirmModal from './ConfirmModal';

const DOMAINS = [
  { key: 'financial', name: 'Financial', description: 'Pricing, invoicing, revenue actions', thresholdKey: 'max_transaction_amount', thresholdLabel: 'Max transaction amount', tooltip: 'Controls autonomy for price adjustments, invoice creation, and other revenue-impacting actions' },
  { key: 'operational', name: 'Operational', description: 'Inventory, tasks, supplier actions', thresholdKey: 'max_inventory_reorder_value', thresholdLabel: 'Max reorder value', tooltip: 'Controls autonomy for inventory reorders, task creation, and supplier management' },
  { key: 'customer', name: 'Customer', description: 'Follow-ups, surveys, communication', thresholdKey: null, thresholdLabel: null, tooltip: 'Controls autonomy for customer emails, follow-ups, and satisfaction surveys (irreversible actions)' },
  { key: 'learning_growth', name: 'Learning & Growth', description: 'Training, capability development', thresholdKey: null, thresholdLabel: null, tooltip: 'Controls autonomy for training recommendations and capability development actions' },
];

const LEVELS = [
  { level: 0, label: 'L0 Monitor Only', color: '#6c757d', tooltip: 'Causeway observes and analyzes but takes no action' },
  { level: 1, label: 'L1 Recommend', color: '#28a745', tooltip: 'Causeway suggests actions for you to consider — no drafts or execution' },
  { level: 2, label: 'L2 Draft & Await', color: '#007bff', tooltip: 'Causeway drafts actions and waits for your approval before executing' },
  { level: 3, label: 'L3 Act & Notify', color: '#ffc107', tooltip: 'Causeway executes actions within safety limits and notifies you afterward' },
  { level: 4, label: 'L4 Full Autonomy', color: '#dc3545', tooltip: 'Causeway acts independently — use with caution, all safety limits still apply' },
];

interface AutonomySectionProps {
  companyId: number;
  profile: any;
  onProfileChange: (profile: any) => void;
  onSave: () => Promise<void>;
}

const AutonomySection: React.FC<AutonomySectionProps> = ({
  profile,
  onProfileChange,
  onSave,
}) => {
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const domainLevels = profile?.domain_levels || {};

  function getDomainLevel(domainKey: string): number {
    return domainLevels[domainKey]?.level ?? 0;
  }

  function getThreshold(domainKey: string, thresholdKey: string): number {
    return domainLevels[domainKey]?.thresholds?.[thresholdKey] ?? 0;
  }

  function setDomainLevel(domainKey: string, level: number) {
    const updated = {
      ...profile,
      domain_levels: {
        ...domainLevels,
        [domainKey]: {
          ...domainLevels[domainKey],
          level,
          thresholds: domainLevels[domainKey]?.thresholds || {},
        },
      },
    };
    onProfileChange(updated);
  }

  function setThreshold(domainKey: string, thresholdKey: string, value: number) {
    const updated = {
      ...profile,
      domain_levels: {
        ...domainLevels,
        [domainKey]: {
          ...domainLevels[domainKey],
          thresholds: {
            ...(domainLevels[domainKey]?.thresholds || {}),
            [thresholdKey]: value,
          },
        },
      },
    };
    onProfileChange(updated);
  }

  async function handleSave() {
    setShowConfirm(false);
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await onSave();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-section">
      <p className="settings-description">
        Set how much autonomy Causeway has in each strategic domain. Higher levels allow more independent action.
      </p>

      <div className="autonomy-legend">
        {LEVELS.map((l) => (
          <span key={l.level} className="autonomy-legend-item" style={{ borderColor: l.color }}>
            {l.label}
          </span>
        ))}
      </div>

      {DOMAINS.map((domain) => {
        const level = getDomainLevel(domain.key);
        const levelInfo = LEVELS[level];
        const showThreshold = domain.thresholdKey && level >= 2;

        return (
          <div key={domain.key} className="autonomy-card">
            <div className="autonomy-card-header">
              <div>
                <div className="autonomy-domain-name" data-tooltip={domain.tooltip}>{domain.name}</div>
                <div className="autonomy-domain-desc">{domain.description}</div>
              </div>
              <span
                className="autonomy-level-badge"
                style={{ background: levelInfo.color, color: level === 3 ? '#333' : '#fff' }}
              >
                {levelInfo.label}
              </span>
            </div>

            <div className="autonomy-slider">
              <div className="autonomy-track">
                <div
                  className="autonomy-track-fill"
                  style={{ width: `${(level / 4) * 100}%`, background: levelInfo.color }}
                />
              </div>
              <div className="autonomy-stops">
                {LEVELS.map((l) => (
                  <button
                    key={l.level}
                    className={`autonomy-stop ${l.level <= level ? 'autonomy-stop-active' : ''} ${l.level === level ? 'autonomy-stop-current' : ''}`}
                    style={l.level === level ? { borderColor: levelInfo.color, background: levelInfo.color } : {}}
                    onClick={() => setDomainLevel(domain.key, l.level)}
                    aria-label={l.label}
                    data-tooltip={l.tooltip}
                  />
                ))}
              </div>
              <div className="autonomy-labels">
                {LEVELS.map((l) => (
                  <span key={l.level}>L{l.level}</span>
                ))}
              </div>
            </div>

            {showThreshold && domain.thresholdKey && (
              <div className="autonomy-threshold">
                <label className="autonomy-threshold-label">{domain.thresholdLabel}:</label>
                <div className="autonomy-threshold-input">
                  <span className="autonomy-dollar">$</span>
                  <input
                    type="number"
                    value={getThreshold(domain.key, domain.thresholdKey) || ''}
                    onChange={(e) =>
                      setThreshold(domain.key, domain.thresholdKey!, parseFloat(e.target.value) || 0)
                    }
                    className="settings-input"
                    min={0}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      {saveError && <div className="settings-error">{saveError}</div>}
      {saveSuccess && <div className="settings-success">Settings saved successfully.</div>}

      <div className="settings-save-bar">
        <button
          className="settings-btn settings-btn-primary"
          onClick={() => setShowConfirm(true)}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {showConfirm && (
        <ConfirmModal
          title="Confirm Changes"
          message="Are you sure you want to save these settings? Changes will take effect immediately."
          onConfirm={handleSave}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
};

export default AutonomySection;
